/* eslint-disable jsx-a11y/heading-has-content, jsx-a11y/anchor-has-content -- react-markdown supplies children through component props */
import Markdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import ReactPlayer from "react-player";
import { useEffect } from "react";
import TableOfContents from "../components/TableOfContents";
// import JamDemo from "../assets/explorations/Jam-YCF25_Demo.mp4";

const images = {
	// JamDemo,
};

const content1 = `
As I enter my senior year at Georgia Tech, I've been reflecting on my work, particularly the AI-native web browser called Blossom[#blossom] I spent the last two years building alongside my good friend Avaye Dawadi.
Although we stopped development, there were quite a few features and paradigms that we did not get a chance to fully explore. We've especially been fascinated by the idea of proactive AI, which we believe to be the next generation of AI UI.

This page serves as a running log of our explorations since July 2025. Today, we're prototyping a Swift-based background app that — in conjunction with a Chrome extension — can automatically detect the user's next step in the browser and complete it for them.
Starting off small, we're currently improving a flow to simply but intelligently paste copied content across multiple inputs, similar to a smart Autofill. But we're confident that proactive AI, if executed with low intrusiveness and high relevance, will complement current chat interfaces to augment productivity for all consumers and professionals.
# Background
## Inspirations
"God is a Parasite" by Jason Yuan
[https://jasonyuan.substack.com/p/god-is-a-parasite](https://jasonyuan.substack.com/p/god-is-a-parasite)
Much of my thinking around proactive AI is grounded in this beautiful essay by Jason Yuan, cofounder of New Computer. Although I urge you to read it in full, here are a few key quotes that have stuck with me:
> ""
> ""
> ""

## "Jam"
`;

const content2 = `
`;

const Explorations = () => {
	useEffect(() => {
		window.scrollTo(0, 0);
	}, []);

	// Combine all content for TableOfContents
	const allContent = [content1, content2];

	return (
		<div
			className="flex flex-col w-full min-h-screen items-center gap-y-8 lg:gap-y-20 p-4 lg:p-20 bg-[#ffedfc]"
			style={{
				paddingLeft: "max(1.25rem, calc(var(--toc-right-edge, 0px) + 1rem))",
			}}
		>
			<TableOfContents
				content={allContent}
				pageName="Explorations"
				accentColor="#f1a2d9"
				darkMode={false}
			/>
			<div className="flex flex-col lg:flex-row header w-full max-w-[1000px] bg-[#f1a2d9] p-4 lg:p-20 rounded-3xl bg-gradient-to-b from-white/20 via-white/50 to-white/80 shadow-[0_0_0_1px_rgba(0,0,0,0.08),0_-2px_1px_0_rgba(0,0,0,0.08)_inset,0_2px_1px_0_rgba(255,255,255,0.5)_inset,0_2px_5px_-1px_rgba(0,0,0,0.05),0_1px_3px_-1px_rgba(0,0,0,0.3)] border border-[#ff00f2]/20">
				<div className="flex flex-col w-full lg:min-w-[400px] lg:max-w-[400px]">
					<h1 className="text-6xl lg:text-[120px] font-medium mb-4 tracking-[-0.04em] lg:mt-0 mt-12">
						Explorations
					</h1>
					<div className="details grid grid-cols-2 w-full lg:w-[385px] gap-y-2 gap-x-4 text-sm lg:text-base">
						<p className="font-semibold text-right">Project Duration</p>
						<p>July 2025 - Present</p>
						<p className="font-semibold text-right">Team Members</p>
						<div className="flex flex-col leading-tight mt-[2px]">
							<a
								href="https://avaye.netlify.app/"
								className="hover:underline"
								target="_blank"
								rel="noopener noreferrer"
							>
								Avaye Dawadi
							</a>
						</div>
						<p className="font-semibold text-right">Tools Used</p>
						<div className="flex flex-col leading-tight mt-[2px]">
							<p>SwiftUI</p>
							<p>Electron</p>
							<p>Chrome Extensions</p>
							<p>LLM APIs</p>
						</div>
					</div>
				</div>
				{/* <div className="flex w-full items-center justify-center mt-8 lg:mt-0">
					<img
						src={heroImage}
						alt="hero"
						className="object-contain w-full max-w-[300px] lg:max-w-[500px] rounded-2xl lg:ml-20"
					/>
				</div> */}
			</div>
			<div className="body w-full pt-8 lg:pt-10 max-w-[1000px] p-4 lg:p-20 bg-[#ffedfc] rounded-3xl">
				<Markdown
					rehypePlugins={[rehypeRaw]}
					components={{
						h1: ({ node, ...props }) => {
							const text =
								typeof props.children === "string" ? props.children : "";
							const id = text
								.toLowerCase()
								.replace(/[^a-z0-9]+/g, "-")
								.replace(/(^-|-$)/g, "");
							return (
								<h1
									id={id}
									className="text-3xl lg:text-4xl font-bold my-6 lg:my-8 text-center"
									{...props}
								/>
							);
						},
						h2: ({ node, ...props }) => {
							const text =
								typeof props.children === "string" ? props.children : "";
							const id = text
								.toLowerCase()
								.replace(/[^a-z0-9]+/g, "-")
								.replace(/(^-|-$)/g, "");
							return (
								<h2
									id={id}
									className="text-2xl lg:text-3xl font-bold mb-4 lg:mb-6 mt-4"
									{...props}
								/>
							);
						},
						h3: ({ node, ...props }) => {
							const text =
								typeof props.children === "string" ? props.children : "";
							const id = text
								.toLowerCase()
								.replace(/[^a-z0-9]+/g, "-")
								.replace(/(^-|-$)/g, "");
							return (
								<h3
									id={id}
									className="text-xl lg:text-2xl font-semibold mb-2 "
									{...props}
								/>
							);
						},
						h4: ({ node, ...props }) => (
							<h4
								className="text-xl lg:text-2xl font-semibold mb-2 leading-tight"
								{...props}
							/>
						),
						h5: ({ node, ...props }) => (
							<h5 className="text-xl lg:text-2xl font-medium mb-2" {...props} />
						),
						h6: ({ node, ...props }) => (
							<h6
								className="text-xs lg:text-sm italic text-center mt-[-16px] lg:mt-[-32px] mb-6 lg:mb-8"
								{...props}
							/>
						),
						p: ({ node, ...props }) => (
							<p
								className="mb-4 leading-tight text-neutral-800 text-sm lg:text-base"
								{...props}
							/>
						),
						img: ({ node, ...props }) => {
							const imageSrc = images[props.src] || props.src;
							if (imageSrc.endsWith(".mp4") || imageSrc.endsWith(".mov")) {
								return (
									<ReactPlayer
										url={imageSrc}
										controls
										className="my-8 lg:my-12 rounded-2xl mx-auto w-full"
										width="100%"
										height="auto"
									/>
								);
							}
							return (
								<img
									src={imageSrc}
									alt={props.alt}
									className="w-full h-auto object-contain my-8 lg:my-12 max-h-[300px] lg:max-h-[500px] rounded-2xl mx-auto"
								/>
							);
						},
						a: ({ node, ...props }) => (
							<a
								className="text-[#FF5C00] hover:text-[#FF5C00] underline"
								target="_blank"
								rel="noopener noreferrer"
								{...props}
							/>
						),
						ul: ({ node, ...props }) => (
							<ul className="list-disc list-inside mb-4 ml-4" {...props} />
						),
					}}
				>
					{content1}
				</Markdown>
				<ReactPlayer
					url={images.JamDemo}
					controls
					className="my-8 lg:my-12 rounded-2xl mx-auto w-full"
					width="100%"
					height="auto"
				/>
				<Markdown
					rehypePlugins={[rehypeRaw]}
					components={{
						h1: ({ node, ...props }) => {
							const text =
								typeof props.children === "string" ? props.children : "";
							const id = text
								.toLowerCase()
								.replace(/[^a-z0-9]+/g, "-")
								.replace(/(^-|-$)/g, "");
							return (
								<h1
									id={id}
									className="text-3xl lg:text-4xl font-bold my-6 lg:my-8 text-center"
									{...props}
								/>
							);
						},
						h2: ({ node, ...props }) => {
							const text =
								typeof props.children === "string" ? props.children : "";
							const id = text
								.toLowerCase()
								.replace(/[^a-z0-9]+/g, "-")
								.replace(/(^-|-$)/g, "");
							return (
								<h2
									id={id}
									className="text-2xl lg:text-3xl font-bold mb-4 lg:mb-6 mt-4"
									{...props}
								/>
							);
						},
						h3: ({ node, ...props }) => {
							const text =
								typeof props.children === "string" ? props.children : "";
							const id = text
								.toLowerCase()
								.replace(/[^a-z0-9]+/g, "-")
								.replace(/(^-|-$)/g, "");
							return (
								<h3
									id={id}
									className="text-xl lg:text-2xl font-semibold mb-2 "
									{...props}
								/>
							);
						},
						h4: ({ node, ...props }) => (
							<h4
								className="text-xl lg:text-2xl font-semibold mb-2 leading-tight"
								{...props}
							/>
						),
						h5: ({ node, ...props }) => (
							<h5 className="text-xl lg:text-2xl font-medium mb-2" {...props} />
						),
						h6: ({ node, ...props }) => (
							<h6
								className="text-xs lg:text-sm italic text-center mt-[-16px] lg:mt-[-32px] mb-6 lg:mb-8"
								{...props}
							/>
						),
						p: ({ node, ...props }) => (
							<p
								className="mb-4 leading-tight text-neutral-800 text-sm lg:text-base"
								{...props}
							/>
						),
						img: ({ node, ...props }) => {
							const imageSrc = images[props.src] || props.src;
							if (imageSrc.endsWith(".mp4") || imageSrc.endsWith(".mov")) {
								return (
									<ReactPlayer
										url={imageSrc}
										controls
										className="my-8 lg:my-12 rounded-2xl mx-auto w-full"
										width="100%"
										height="auto"
									/>
								);
							}
							return (
								<img
									src={imageSrc}
									alt={props.alt}
									className="w-full h-auto object-contain my-8 lg:my-12 max-h-[300px] lg:max-h-[500px] rounded-2xl mx-auto"
								/>
							);
						},
						a: ({ node, ...props }) => (
							<a
								className="text-[#FF5C00] hover:text-[#FF5C00] underline"
								target="_blank"
								rel="noopener noreferrer"
								{...props}
							/>
						),
						ul: ({ node, ...props }) => (
							<ul className="list-disc list-inside mb-4 ml-4" {...props} />
						),
					}}
				>
					{content2}
				</Markdown>
			</div>
		</div>
	);
};

export default Explorations;
